import { useContext, useEffect, useState } from "react";
import { BreadTransaction } from "breadcommon";
import { buildClasses } from "../utils/buildClasses";
import { ScrollContainerContext } from "../rye/RyeMainContentContainerBase";
import { RyeChartTooltip } from "../rye/RyeChartTooltip";
import { DollarAmount } from "../common/DollarAmount";
import type { BreakdownType } from "./useCategoryBreakdownData";

/** Shared by the Category breakdown charts. */

export const TYPE_LABELS: Record<BreakdownType, string> = {
  expenses: "Expenses",
  income: "Income",
  savings: "Savings",
};

// Each type keeps its usual color, in shades from darkest (the largest) to
// lightest, so a chart reads as one type while its pieces stay apart. Hex
// values match the Tailwind tokens in tailwind.config.js; Nivo needs real
// colors, not class names.
const SHADES: Record<BreakdownType, string[]> = {
  // blue (sky) 800 through 200
  expenses: [
    "#075985",
    "#0369a1",
    "#0284c7",
    "#0ea5e9",
    "#38bdf8",
    "#7dd3fc",
    "#bae6fd",
  ],
  // green 800 through 200
  income: [
    "#155636",
    "#208352",
    "#2AAC6B",
    "#3FD088",
    "#63D99E",
    "#8CE3B8",
    "#B1ECCF",
  ],
  // purple 800 through 200
  savings: [
    "#4c18bf",
    "#5b1ee3",
    "#6930f7",
    "#7d57ff",
    "#9a85ff",
    "#bcb1ff",
    "#d8d4ff",
  ],
};
// From this shade on, white text stops being readable on top.
const FIRST_LIGHT_SHADE = 4;
export const OTHER_COLOR = "#cbd5e1"; // on-surface-300
const DARK_TEXT_COLOR = "#1e293b"; // on-surface-800

/** The shade for the piece at `rank`, 0 being the largest. */
export function shadeFor(type: BreakdownType, rank: number): string {
  const shades = SHADES[type];
  return shades[Math.min(rank, shades.length - 1)];
}

/** Readable text color on top of the shade for `rank`. */
export function textColorFor(rank: number): string {
  return rank >= FIRST_LIGHT_SHADE ? DARK_TEXT_COLOR : "#ffffff";
}

export function percentOf(value: number, total: number): string {
  if (total <= 0) return "";
  const percent = (value / total) * 100;
  // A real piece rounding to 0% would read as nothing at all.
  return percent < 1 ? "<1%" : `${Math.round(percent)}%`;
}

/** What a click on a chart opens in the transactions pane. */
export interface BreakdownSelection {
  id: string;
  label: string;
  transactions: BreadTransaction[];
}

// The card's shared 18px padding above and below the chart.
const CHART_VERTICAL_PADDING_PX = 36;

/**
 * The height of the card's visible area, for charts that have no intrinsic
 * height. It doesn't change as the chart inside resizes, so there's no
 * feedback loop. Only works inside the card, through ScrollContainerContext.
 */
export function useAvailableChartHeight(): number {
  const scrollContainer = useContext(ScrollContainerContext);
  const [availableHeightPx, setAvailableHeightPx] = useState(0);
  useEffect(() => {
    if (!scrollContainer) return;
    const measure = () =>
      setAvailableHeightPx(
        scrollContainer.clientHeight - CHART_VERTICAL_PADDING_PX,
      );
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scrollContainer);
    return () => observer.disconnect();
  }, [scrollContainer]);
  return availableHeightPx;
}

export const CHART_TOOLTIP_THEME = {
  tooltip: {
    container: {
      background: "transparent",
      boxShadow: "none",
      padding: 0,
    },
  },
};

export function BreakdownTooltip({
  color,
  label,
  value,
  total,
  signed = false,
  currency,
}: {
  color: string;
  label: string;
  value: number;
  /** When given, the value's share of it is shown too. */
  total?: number;
  /** Show the value's sign, for amounts that can go either way. */
  signed?: boolean;
  currency: string;
}): JSX.Element {
  return (
    <RyeChartTooltip>
      <div
        className={buildClasses(
          "flex",
          "items-center",
          "gap-2",
          "bg-surface",
          "border",
          "border-surface-600",
          "shadow-glow",
          "rounded-md",
          "px-3",
          "py-2",
          "text-sm",
          "text-on-surface-700",
        )}
      >
        <span
          className={buildClasses("w-2.5", "h-2.5", "rounded-sm")}
          style={{ backgroundColor: color }}
        />
        <span>{label}</span>
        <span className={buildClasses("font-medium")}>
          <DollarAmount
            n={value}
            size="sm"
            showDollarSign={true}
            showMinus={signed}
            showPlus={signed}
            align="left"
            currencyCode={currency}
          />
        </span>
        {total !== undefined && (
          <span className={buildClasses("text-on-surface-500")}>
            {percentOf(value, total)}
          </span>
        )}
      </div>
    </RyeChartTooltip>
  );
}

export function UnreviewedNote({
  count,
  className,
}: {
  count: number;
  className?: string;
}): JSX.Element | null {
  if (count === 0) return null;
  return (
    <span
      className={buildClasses(
        "text-xs",
        "text-on-surface-500",
        className ?? "",
      )}
    >
      Ignores {count.toLocaleString()} unreviewed{" "}
      {count === 1 ? "transaction" : "transactions"}
    </span>
  );
}
